/**
 * Multi-Select Download Manager
 * Handles bulk download functionality for product collections
 */
class MultiSelectDownloadManager {
  constructor() {
    this.state = {
      multiSelectMode: false,
      selectedProducts: new Set(),
      floatingButton: null,
      buttonContainer: null,
      stickyBulkButton: null,
      cancelButton: null,
      originalButtonText: null,
      originalButtonClick: null,
      isScrolling: false,
      lastScrollY: 0
    };

    this.bulkDownloader = new BulkDownloader();
    this.mediaPreferenceSelector = new MediaPreferenceLinkSelector();

    // Bind methods to maintain context
    this.updateStickyButtonVisibility = this.updateStickyButtonVisibility.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  /**
   * Trial and limits helpers
   */
  isTrialing() {
    return document.body.classList.contains('plan-status-trialing');
  }

  getTrialDownloadLimit() {
    // Mirror logic from assets/global.js
    // Default free trial limit
    let limit = 50;
    if (document.body.classList.contains('plan-Pro')) {
      limit = 75;
    }
    return limit;
  }

  getDownloadsUsed() {
    // Parse body class like `downloads-12`
    const cls = Array.from(document.body.classList).find(c => c.startsWith('downloads-'));
    if (!cls) return 0;
    const n = parseInt(cls.split('-')[1], 10);
    return isNaN(n) ? 0 : n;
  }

  getTrialDownloadsRemaining() {
    if (!this.isTrialing()) return Infinity;
    const remaining = this.getTrialDownloadLimit() - this.getDownloadsUsed();
    return Math.max(0, remaining);
  }

  /**
   * Check if the page has the required elements for bulk download
   */
  hasRequiredElements() {
    return document.querySelector('.product-additional-section') !== null;
  }

  /**
   * Initialize the multi-select functionality
   */
  init() {
    // Only initialize if the page has the required elements
    if (!this.hasRequiredElements()) {
      return;
    }

    this.createStickyBulkButton();
    this.updateStickyButtonVisibility(); // Set initial visibility
    this.setupScrollListener();
    this.bindGlobalEvents();
  }

  /**
   * Bind global event listeners
   */
  bindGlobalEvents() {
    // Handle escape key to exit bulk mode
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.state.multiSelectMode) {
        this.disableMultiSelectMode();
      }
    });
  }

  /**
   * Setup event listeners for the download button
   */
  setupEventListeners() {
    const downloadButton = document.querySelector('.download-media-collection');
    if (downloadButton) {
      this.state.originalButtonText = downloadButton.innerHTML;
      this.state.originalButtonClick = downloadButton.onclick;
      // Restore original scroll behavior
      downloadButton.onclick = () => this.scrollToProductGrid();
    }
  }

  /**
   * Scroll to product grid (original behavior)
   */
  scrollToProductGrid() {
    const productGrid = document.getElementById('related-products-grid');
    if (productGrid) {
      const navbarHeight = document.querySelector('.product-additional-section')?.offsetHeight || 0;
      const targetPosition = productGrid.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Create sticky bulk download button
   */
  createStickyBulkButton() {
    if (this.state.stickyBulkButton) return;

    this.state.stickyBulkButton = this.createButton({
      id: 'sticky-bulk-download-button',
      className: 'sticky-bulk-download-button',
      innerHTML: this.getBulkDownloadButtonHTML(),
      onClick: () => this.enableMultiSelectMode()
    });

    // Position the button at the first group-top
    this.positionButtonAtFirstGroup();

    // Append to the related-products-section for proper containment
    const relatedProductsSection = document.getElementById('related-products-section');
    if (relatedProductsSection) {
      relatedProductsSection.appendChild(this.state.stickyBulkButton);
    } else {
      // Fallback to document body if section not found
      document.body.appendChild(this.state.stickyBulkButton);
    }
  }

  /**
   * Create a standardized button element
   */
  createButton({ id, className, innerHTML, onClick }) {
    const button = document.createElement('button');
    if (id) button.id = id;
    if (className) button.className = className;
    if (innerHTML) button.innerHTML = innerHTML;
    if (onClick) button.addEventListener('click', onClick);
    return button;
  }

  /**
   * Position button at the first group-top
   */
  positionButtonAtFirstGroup() {
    const relatedProductsSection = document.getElementById('related-products-section');
    const firstGroupTop = document.querySelector('.group-top');
    if (firstGroupTop && this.state.stickyBulkButton && relatedProductsSection) {
      const sectionRect = relatedProductsSection.getBoundingClientRect();
      const groupRect = firstGroupTop.getBoundingClientRect();
      const button = this.state.stickyBulkButton;

      // Calculate position relative to the section container
      const relativeTop = groupRect.top - sectionRect.top;

      // Clear any previous styles and set absolute positioning
      button.style.position = 'absolute';
      button.style.top = relativeTop + 'px';
      button.style.left = 'auto';
      button.style.right = '20px';
      button.classList.remove('sticky-top');

      // Update cached scroll position
      this.state.lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    }
  }

  /**
   * Get HTML for bulk download button
   */
  getBulkDownloadButtonHTML() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
        <path d="M21.4004 14.25V19.5C21.4004 19.8978 21.2424 20.2794 20.9611 20.5607C20.6797 20.842 20.2982 21 19.9004 21H4.90039C4.50257 21 4.12103 20.842 3.83973 20.5607C3.55843 20.2794 3.40039 19.8978 3.40039 19.5V14.25C3.40039 14.0511 3.47941 13.8603 3.62006 13.7197C3.76071 13.579 3.95148 13.5 4.15039 13.5C4.3493 13.5 4.54007 13.579 4.68072 13.7197C4.82137 13.8603 4.90039 14.0511 4.90039 14.25V19.5H19.9004V14.25C19.9004 14.0511 19.9794 13.8603 20.1201 13.7197C20.2607 13.579 20.4515 13.5 20.6504 13.5C20.8493 13.5 21.0401 13.579 21.1807 13.7197C21.3214 13.8603 21.4004 14.0511 21.4004 14.25ZM11.8698 14.7806C11.9394 14.8504 12.0221 14.9057 12.1132 14.9434C12.2042 14.9812 12.3018 15.0006 12.4004 15.0006C12.499 15.0006 12.5965 14.9812 12.6876 14.9434C12.7786 14.9057 12.8614 14.8504 12.931 14.7806L16.681 11.0306C16.7507 10.9609 16.806 10.8782 16.8437 10.7872C16.8814 10.6961 16.9008 10.5985 16.9008 10.5C16.9008 10.4015 16.8814 10.3039 16.8437 10.2128C16.806 10.1218 16.7507 10.0391 16.681 9.96937C16.6113 9.89969 16.5286 9.84442 16.4376 9.8067C16.3465 9.76899 16.2489 9.74958 16.1504 9.74958C16.0518 9.74958 15.9543 9.76899 15.8632 9.8067C15.7722 9.84442 15.6894 9.89969 15.6198 9.96937L13.1504 12.4397V3.75C13.1504 3.55109 13.0714 3.36032 12.9307 3.21967C12.7901 3.07902 12.5993 3 12.4004 3C12.2015 3 12.0107 3.07902 11.8701 3.21967C11.7294 3.36032 11.6504 3.55109 11.6504 3.75V12.4397L9.18102 9.96937C9.04029 9.82864 8.84941 9.74958 8.65039 9.74958C8.45137 9.74958 8.2605 9.82864 8.11977 9.96937C7.97903 10.1101 7.89997 10.301 7.89997 10.5C7.89997 10.699 7.97903 10.8899 8.11977 11.0306L11.8698 14.7806Z" fill="white"/>
      </svg>
      Bulk Download
    `;
  }

  /**
   * Setup scroll listener to show/hide sticky button
   */
  setupScrollListener() {
    // Throttled scroll handler using requestAnimationFrame
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Also update on window resize to handle dynamic content changes
    window.addEventListener('resize', () => {
      // Use requestAnimationFrame for resize as well
      if (!this.state.isScrolling) {
        requestAnimationFrame(() => {
          this.updateStickyButtonVisibility();
        });
      }
    });
  }

  /**
   * Handle scroll events with throttling
   */
  handleScroll() {
    if (this.state.isScrolling) return;

    this.state.isScrolling = true;

    requestAnimationFrame(() => {
      this.updateStickyUI();
      this.state.isScrolling = false;
    });
  }

  /**
   * Sticky helpers (single source of truth)
   */
  getStickyContext() {
    const section = document.getElementById('related-products-section');
    const firstGroup = document.querySelector('.group-top');
    if (!section || !firstGroup) return null;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const sectionRect = section.getBoundingClientRect();
    const groupRect = firstGroup.getBoundingClientRect();
    const groupTopPosition = groupRect.top + scrollTop;
    const sectionBottom = sectionRect.bottom + scrollTop;
    const stickyHeader = document.querySelector('.shopify-section-header-sticky');
    const headerVisible = !!stickyHeader && !stickyHeader.classList.contains('shopify-section-header-hidden');

    return { section, firstGroup, scrollTop, sectionRect, groupRect, groupTopPosition, sectionBottom, headerVisible };
  }

  updateStickyFor(element) {
    if (!element) return;
    const ctx = this.getStickyContext();
    if (!ctx) return;

    const shouldBeSticky = ctx.scrollTop > ctx.groupTopPosition && ctx.scrollTop < ctx.sectionBottom - 100;

    if (shouldBeSticky) {
      element.style.position = '';
      element.style.top = '';
      element.style.left = '';
      element.style.right = '';
      element.classList.add('sticky-top');
      if (ctx.headerVisible) {
        element.classList.add('with-sticky-header');
      } else {
        element.classList.remove('with-sticky-header');
      }
    } else {
      element.classList.remove('sticky-top', 'with-sticky-header');
      const relativeTop = Math.max(0, ctx.groupRect.top - ctx.sectionRect.top);
      element.style.position = 'absolute';
      element.style.top = relativeTop + 'px';
      element.style.left = 'auto';
      element.style.right = '20px';
    }
  }

  updateStickyUI() {
    if (!this.state.multiSelectMode) {
      this.updateStickyFor(this.state.stickyBulkButton);
    }
    this.updateStickyFor(this.state.buttonContainer);
  }

  // Backward compat alias for existing calls
  updateStickyButtonVisibility() {
    this.updateStickyUI();
  }

  /**
   * Enable multi-select mode
   */
  enableMultiSelectMode() {
    console.log('Enabling multi-select mode...');
    this.setMultiSelectMode(true);
    this.initializeBulkMode();
    this.animateButtonsIn();
  }

  /**
   * Disable multi-select mode
   */
  disableMultiSelectMode() {
    console.log('Disabling multi-select mode...');
    this.animateButtonsOut();
    this.cleanupBulkMode();
    this.setMultiSelectMode(false);
  }

  /**
   * Set multi-select mode state
   */
  setMultiSelectMode(enabled) {
    this.state.multiSelectMode = enabled;
    this.state.selectedProducts.clear();
  }

  /**
   * Initialize bulk mode UI and functionality
   */
  initializeBulkMode() {
    this.hideStickyBulkButton();
    this.addCheckboxesToProducts();
    this.makeProductItemsSelectable();
    this.disableHoverOverlay();
    this.createFloatingDownloadButton();
    this.createCancelButton();
  }

  /**
   * Clean up bulk mode UI and functionality
   */
  cleanupBulkMode() {
    this.showStickyBulkButton();
    this.removeCheckboxes();
    this.removeProductItemSelectability();
    this.enableHoverOverlay();
    this.removeFloatingButton();
    this.removeCancelButton();
  }

  /**
   * Animate buttons entering the view
   */
  animateButtonsIn() {
    setTimeout(() => {
      if (this.state.buttonContainer) {
        this.state.buttonContainer.classList.add('entering');
      }
    }, 10);
  }

  /**
   * Animate buttons exiting the view
   */
  animateButtonsOut() {
    if (this.state.buttonContainer) {
      this.state.buttonContainer.classList.remove('entering');
      this.state.buttonContainer.classList.add('exiting');
    }

    // Wait for animation to complete before cleanup
    setTimeout(() => {
      this.cleanupBulkMode();
    }, 300);
  }

  /**
   * Hide sticky bulk button
   */
  hideStickyBulkButton() {
    if (this.state.stickyBulkButton) {
      this.state.stickyBulkButton.style.display = 'none';
    }
  }

  /**
   * Show sticky bulk button
   */
  showStickyBulkButton() {
    if (this.state.stickyBulkButton) {
      // Reset button state first
      this.state.stickyBulkButton.classList.remove('sticky-top', 'with-sticky-header');
      this.state.stickyBulkButton.style.display = 'flex';

      // Check if we should immediately make it sticky based on current scroll position
      const relatedProductsSection = document.getElementById('related-products-section');
      const firstGroupTop = document.querySelector('.group-top');
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (relatedProductsSection && firstGroupTop) {
        const groupRect = firstGroupTop.getBoundingClientRect();
        const sectionRect = relatedProductsSection.getBoundingClientRect();
        const groupTopPosition = groupRect.top + scrollTop;
        const sectionBottom = sectionRect.bottom + scrollTop;

        // If user has scrolled past the first group, make button sticky immediately
        if (scrollTop > groupTopPosition && scrollTop < sectionBottom - 100) {
          // Make it sticky immediately
          this.state.stickyBulkButton.classList.add('sticky-top');

          // Check for sticky header
          const stickyHeader = document.querySelector('.shopify-section-header-sticky');
          const isStickyHeaderVisible = stickyHeader && !stickyHeader.classList.contains('shopify-section-header-hidden');
          if (isStickyHeaderVisible) {
            this.state.stickyBulkButton.classList.add('with-sticky-header');
          }
        } else {
          // Position at first group if not scrolled past it
          this.state.stickyBulkButton.style.position = 'absolute';
          this.positionButtonAtFirstGroup();
        }
      } else {
        // Fallback to positioning at first group
        this.state.stickyBulkButton.style.position = 'absolute';
        this.positionButtonAtFirstGroup();
      }

      // Update visibility after positioning
      setTimeout(() => {
        this.updateStickyButtonVisibility();
      }, 10);
    }
  }

  /**
   * Add checkboxes to all product items
   */
  addCheckboxesToProducts() {
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      if (!item.querySelector('.product-checkbox')) {
        const checkbox = this.createCheckbox();
        item.classList.add('bulk-mode-relative');
        item.appendChild(checkbox);
      }
    });
  }

  /**
   * Create a checkbox element
   */
  createCheckbox() {
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'product-checkbox';
    // Prevent item click handler from toggling the checkbox a second time
    checkbox.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    checkbox.addEventListener('change', (event) => this.handleProductSelection(event));
    return checkbox;
  }

  /**
   * Remove all checkboxes
   */
  removeCheckboxes() {
    const checkboxes = document.querySelectorAll('.product-checkbox');
    checkboxes.forEach(checkbox => checkbox.remove());

    // Remove selected state and bulk mode classes from all items
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      item.classList.remove('selected', 'bulk-mode-relative');
    });
  }

  /**
   * Make entire product items selectable
   */
  makeProductItemsSelectable() {
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      item.classList.add('bulk-mode-selectable');

      // Add click event to entire item
      const clickHandler = (event) => {
        // Ignore clicks directly on the checkbox; the change handler will manage state
        if (event.target.closest('.product-checkbox')) {
          return;
        }
        // Prevent if clicking on links or buttons
        if (event.target.closest('a, button')) {
          event.preventDefault();
          event.stopPropagation();
        }

        const checkbox = item.querySelector('.product-checkbox');
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change'));
        }
      };

      item.addEventListener('click', clickHandler);
      item._bulkModeClickHandler = clickHandler; // Store reference for cleanup
    });
  }

  /**
   * Remove product item selectability
   */
  removeProductItemSelectability() {
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      item.classList.remove('bulk-mode-selectable');

      // Remove click event
      if (item._bulkModeClickHandler) {
        item.removeEventListener('click', item._bulkModeClickHandler);
        delete item._bulkModeClickHandler;
      }
    });
  }

  /**
   * Disable hover overlay on product items
   */
  disableHoverOverlay() {
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      item.classList.add('bulk-mode-no-hover');
    });
  }

  /**
   * Enable hover overlay on product items
   */
  enableHoverOverlay() {
    const productItems = document.querySelectorAll('.product-item');
    productItems.forEach(item => {
      item.classList.remove('bulk-mode-no-hover');
    });
  }

  /**
   * Handle product selection via checkbox
   */
  handleProductSelection(event) {
    const productItem = event.target.closest('.product-item');
    const productId = productItem.getAttribute('data-product-id');

    // Enforce selection cap for trial users
    if (event.target.checked && this.isTrialing()) {
      const remaining = this.getTrialDownloadsRemaining();
      const currentSelected = this.state.selectedProducts.size;
      if (currentSelected + 1 > remaining) {
        // Revert check and inform user
        event.target.checked = false;
        const msg = remaining === 0
          ? "You've reached your free trial download limit. Upgrade to unlock unlimited access."
          : `You can only select ${remaining} more item${remaining === 1 ? '' : 's'} in your free trial.`;
        this.showError(
          msg,
          "/account?tab=membership",
          remaining === 0 ? "Activate Subscription" : "View Plans"
        );
        return;
      }
    }

    this.toggleProductSelection(productId, event.target.checked);
    this.updateProductItemVisualState(productItem, event.target.checked);
    this.updateFloatingButton();
  }

  /**
   * Toggle product selection
   */
  toggleProductSelection(productId, isSelected) {
    if (isSelected) {
      this.state.selectedProducts.add(productId);
    } else {
      this.state.selectedProducts.delete(productId);
    }
  }

  /**
   * Update product item visual state
   */
  updateProductItemVisualState(productItem, isSelected) {
    if (isSelected) {
      productItem.classList.add('selected');
    } else {
      productItem.classList.remove('selected');
    }
  }

  /**
   * Create floating download button container
   */
  createFloatingDownloadButton() {
    if (this.state.floatingButton) return;

    // Create button container
    this.state.buttonContainer = document.createElement('div');
    this.state.buttonContainer.id = 'bulk-download-button-container';
    this.state.buttonContainer.className = 'bulk-download-button-container';

    // Create download button
    this.state.floatingButton = this.createButton({
      id: 'floating-download-button',
      className: 'floating-download-button',
      onClick: () => this.downloadSelectedProducts()
    });

    // Add button to container
    this.state.buttonContainer.appendChild(this.state.floatingButton);

    this.updateFloatingButton();

    // Position container within related-products-section like the main button
    const relatedProductsSection = document.getElementById('related-products-section');
    if (relatedProductsSection) {
      relatedProductsSection.appendChild(this.state.buttonContainer);
    } else {
      // Fallback to document body if section not found
      document.body.appendChild(this.state.buttonContainer);
    }
  }

  /**
   * Update floating button text
   */
  updateFloatingButton() {
    if (!this.state.floatingButton) return;

    const count = this.state.selectedProducts.size;
    this.state.floatingButton.innerHTML = this.getDownloadButtonHTML(count);

    // Update container positioning to match main button
    // Small delay to ensure container is properly positioned before checking sticky state
    setTimeout(() => {
      this.updateContainerPositioning();
    }, 10);
  }

  /**
   * Update container positioning to match main button behavior
   */
  updateContainerPositioning() {
    if (!this.state.buttonContainer) return;

    const relatedProductsSection = document.getElementById('related-products-section');
    if (!relatedProductsSection) return;

    const firstGroupTop = document.querySelector('.group-top');
    if (!firstGroupTop) return;

    const sectionRect = relatedProductsSection.getBoundingClientRect();
    const groupRect = firstGroupTop.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate positions relative to viewport
    const sectionTop = sectionRect.top + scrollTop;
    const groupTopPosition = groupRect.top + scrollTop;
    const sectionBottom = sectionRect.bottom + scrollTop;

    // Check if sticky header is present and visible
    const stickyHeader = document.querySelector('.shopify-section-header-sticky');
    const hasStickyHeader = !!stickyHeader;
    const isStickyHeaderVisible = hasStickyHeader && !stickyHeader.classList.contains('shopify-section-header-hidden');

    const button = this.state.buttonContainer;
    const shouldBeSticky = scrollTop > groupTopPosition && scrollTop < sectionBottom - 100;

    // Always ensure proper positioning, even if scroll position hasn't changed much
    const isCurrentlySticky = button.classList.contains('sticky-top');

    if (shouldBeSticky) {
      // Clear inline styles and let CSS handle fixed positioning
      button.style.position = '';
      button.style.top = '';
      button.style.left = '';
      button.style.right = '';
      button.classList.add('sticky-top');

      // Handle sticky header offset
      if (isStickyHeaderVisible) {
        button.classList.add('with-sticky-header');
      } else {
        button.classList.remove('with-sticky-header');
      }
    } else {
      button.classList.remove('sticky-top');
      button.classList.remove('with-sticky-header');

      // Set absolute positioning relative to section
      // Ensure the position is always within visible bounds
      const relativeTop = Math.max(0, groupRect.top - sectionRect.top);
      button.style.position = 'absolute';
      button.style.top = relativeTop + 'px';
      button.style.left = 'auto';
      button.style.right = '20px';
    }
  }

  /**
   * Get HTML for download button with count
   */
  getDownloadButtonHTML(count) {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
        <path d="M21.4004 14.25V19.5C21.4004 19.8978 21.2424 20.2794 20.9611 20.5607C20.6797 20.842 20.2982 21 19.9004 21H4.90039C4.50257 21 4.12103 20.842 3.83973 20.5607C3.55843 20.2794 3.40039 19.8978 3.40039 19.5V14.25C3.40039 14.0511 3.47941 13.8603 3.62006 13.7197C3.76071 13.579 3.95148 13.5 4.15039 13.5C4.3493 13.5 4.54007 13.579 4.68072 13.7197C4.82137 13.8603 4.90039 14.0511 4.90039 14.25V19.5H19.9004V14.25C19.9004 14.0511 19.9794 13.8603 20.1201 13.7197C20.2607 13.579 20.4515 13.5 20.6504 13.5C20.8493 13.5 21.0401 13.579 21.1807 13.7197C21.3214 13.8603 21.4004 14.0511 21.4004 14.25ZM11.8698 14.7806C11.9394 14.8504 12.0221 14.9057 12.1132 14.9434C12.2042 14.9812 12.3018 15.0006 12.4004 15.0006C12.499 15.0006 12.5965 14.9812 12.6876 14.9434C12.7786 14.9057 12.8614 14.8504 12.931 14.7806L16.681 11.0306C16.7507 10.9609 16.806 10.8782 16.8437 10.7872C16.8814 10.6961 16.9008 10.5985 16.9008 10.5C16.9008 10.4015 16.8814 10.3039 16.8437 10.2128C16.806 10.1218 16.7507 10.0391 16.681 9.96937C16.6113 9.89969 16.5286 9.84442 16.4376 9.8067C16.3465 9.76899 16.2489 9.74958 16.1504 9.74958C16.0518 9.74958 15.9543 9.76899 15.8632 9.8067C15.7722 9.84442 15.6894 9.89969 15.6198 9.96937L13.1504 12.4397V3.75C13.1504 3.55109 13.0714 3.36032 12.9307 3.21967C12.7901 3.07902 12.5993 3 12.4004 3C12.2015 3 12.0107 3.07902 11.8701 3.21967C11.7294 3.36032 11.6504 3.55109 11.6504 3.75V12.4397L9.18102 9.96937C9.04029 9.82864 8.84941 9.74958 8.65039 9.74958C8.45137 9.74958 8.2605 9.82864 8.11977 9.96937C7.97903 10.1101 7.89997 10.301 7.89997 10.5C7.89997 10.699 7.97903 10.8899 8.11977 11.0306L11.8698 14.7806Z" fill="white"/>
      </svg>
      Download Selected (${count})
    `;
  }

  /**
   * Remove floating download button container
   */
  removeFloatingButton() {
    if (this.state.buttonContainer) {
      this.state.buttonContainer.remove();
      this.state.buttonContainer = null;
      this.state.floatingButton = null;
    }
  }

  /**
   * Create cancel button
   */
  createCancelButton() {
    if (this.state.cancelButton) return;

    this.state.cancelButton = this.createButton({
      id: 'cancel-bulk-button',
      className: 'cancel-bulk-button',
      innerHTML: this.getCancelButtonHTML(),
      onClick: () => this.disableMultiSelectMode()
    });

    // Add cancel button to the container if it exists, otherwise create container
    if (this.state.buttonContainer) {
      this.state.buttonContainer.appendChild(this.state.cancelButton);
    } else {
      // Fallback if container doesn't exist
      document.body.appendChild(this.state.cancelButton);
    }
  }

  /**
   * Get HTML for cancel button
   */
  getCancelButtonHTML() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
  }

  /**
   * Remove cancel button
   */
  removeCancelButton() {
    if (this.state.cancelButton) {
      this.state.cancelButton.remove();
      this.state.cancelButton = null;
    }
  }

  /**
   * Download all selected products
   */
  async downloadSelectedProducts() {
    if (this.state.selectedProducts.size === 0) {
      this.showError('Please select at least one product to download.');
      return;
    }

    // Validate against remaining trial allowance before starting downloads
    if (this.isTrialing()) {
      const remaining = this.getTrialDownloadsRemaining();
      const want = this.state.selectedProducts.size;
      if (want > remaining) {
        this.showError(
          `You can download up to ${remaining} more item${remaining === 1 ? '' : 's'} in your free trial. Please reduce your selection.`,
          "/account?tab=membership",
          "Upgrade Now"
        );
        return;
      }
    }

    if (!this.checkPlanAndDownloadLimits()) {
      return;
    }

    const selectedItems = Array.from(this.state.selectedProducts);
    console.log('Starting bulk download for products:', selectedItems);

    this.setDownloadButtonLoading(true);

    try {
      const result = await this.bulkDownloader.downloadProducts(selectedItems);

      console.log(`Download completed. Success: ${result.successCount}, Errors: ${result.errorCount}`);

      if (result.successCount > 0) {
        this.showSuccess(`Successfully downloaded ${result.successCount} items.${result.errorCount > 0 ? ` ${result.errorCount} items failed.` : ''}`);
      } else if (result.errorCount > 0) {
        this.showError(`All ${result.errorCount} downloads failed. Please try again.`);
      }
    } catch (error) {
      console.error('Bulk download error:', error);
      this.showError('An error occurred during bulk download. Please try again.');
    }

    this.setDownloadButtonLoading(false);
    this.disableMultiSelectMode();
  }

  /**
   * Check plan and download limits
   */
  checkPlanAndDownloadLimits() {
    if (window.paidOnlyLocked) {
      this.showError(
        window.paidOnlyPopupText,
        window.paidOnlyCtaUrl,
        "Activate Your Paid Membership"
      );
      return false;
    }

    if (document.body.classList.contains("no-more-downloads")) {
      this.showError(
        "Download limit reached! To unlock unlimited access, you'll need to upgrade to a full membership. Upgrading will conclude your free trial, and membership charges will begin right away.",
        "/account?tab=membership",
        "Activate Subscription"
      );
      return false;
    }

    const pageWidthElement = document.querySelector("#related-products-grid");
    if (
      !(
        pageWidthElement.classList.contains("Pro") ||
        pageWidthElement.classList.contains("Media") ||
        pageWidthElement.classList.contains("Trial") ||
        itemAlreadyOrdered
      ) ||
      document.body.classList.contains("plan-status-canceled")
    ) {
      this.showError("Please add items to cart before downloading.");
      return false;
    }

    return true;
  }

  /**
   * Set download button loading state
   */
  setDownloadButtonLoading(isLoading) {
    if (!this.state.floatingButton) return;

    if (isLoading) {
      this.state.floatingButton.innerHTML = this.getLoadingButtonHTML();
      this.state.floatingButton.disabled = true;
      this.state.floatingButton.classList.add('loading');
    } else {
      this.updateFloatingButton();
      this.state.floatingButton.disabled = false;
      this.state.floatingButton.classList.remove('loading');
    }
  }

  /**
   * Get HTML for loading button
   */
  getLoadingButtonHTML() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;">
        <path d="M21 12a9 9 0 11-6.219-8.56"/>
      </svg>
      Downloading...
    `;
  }

  /**
   * Show error message
   */
  showError(message, link = null, linkText = null) {
    if (typeof showErrorPopup === 'function') {
      showErrorPopup(message, link, linkText);
    } else {
      alert(message);
    }
  }

  /**
   * Show success message
   */
  showSuccess(message) {
    if (typeof showErrorPopup === 'function') {
      showErrorPopup(message);
    } else {
      alert(message);
    }
  }
}

/**
 * Bulk Downloader Class
 * Handles the actual downloading of multiple products
 */
class BulkDownloader {
  constructor() {
    this.successCount = 0;
    this.errorCount = 0;
  }

  /**
   * Download multiple products with delay between downloads
   */
  async downloadProducts(selectedItems) {
    this.successCount = 0;
    this.errorCount = 0;

    console.log(`Starting bulk download of ${selectedItems.length} items`);

    for (let i = 0; i < selectedItems.length; i++) {
      const productId = selectedItems[i];

      try {
        const productItem = document.querySelector(`.product-item[data-product-id="${productId}"]`);

        if (productItem) {
          console.log(`Processing item ${i + 1}/${selectedItems.length} - ID:`, productId);
          const productData = this.extractProductDataFromItem(productItem);

          if (productData) {
            console.log('Extracted product data:', productData);
            await this.downloadProductWithPreferences(productData);
            this.successCount++;

            // Add delay between downloads to prevent browser blocking
            if (i < selectedItems.length - 1) {
              console.log('Waiting 1000ms before next download...');
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } else {
            console.error('Failed to extract product data for ID:', productId);
            this.errorCount++;
          }
        } else {
          console.error('Product item not found for ID:', productId);
          this.errorCount++;
        }
      } catch (error) {
        console.error('Error downloading product:', productId, error);
        this.errorCount++;

        // Continue with next download even if one fails
        if (i < selectedItems.length - 1) {
          console.log('Waiting 1000ms before next download after error...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    console.log(`Bulk download completed. Success: ${this.successCount}, Errors: ${this.errorCount}`);

    return {
      successCount: this.successCount,
      errorCount: this.errorCount
    };
  }

  /**
   * Extract product data from DOM item
   */
  extractProductDataFromItem(productItem) {
    try {
      const productType = productItem.getAttribute('data-type') || productItem.getAttribute('product-type');
      const title = productItem.getAttribute('data-title');
      const producer = productItem.getAttribute('data-producer');

      console.log('Extracting data from item:', { productType, title, producer });
      console.log('Available products:', productPackArray.length);

      const product = productPackArray.find(p =>
        p.title === title &&
        p.productType === productType
      );

      if (product) {
        console.log('Found matching product:', product.title, product.productType);
        return {
          product,
          type: productType,
          title,
          producer: producer || product.akeneoProducerText?.value || ""
        };
      } else {
        console.error('No matching product found for:', { title, productType });
        console.log('Available products:', productPackArray.map(p => ({ title: p.title, type: p.productType })));
      }
    } catch (error) {
      console.error('Error extracting product data:', error);
    }
    return null;
  }

  /**
   * Download product using Media Preferences
   */
  async downloadProductWithPreferences(productData) {
    const { product, type } = productData;

    console.log('Starting download for product:', product.title, 'type:', type);

    if (!product.awsLinkExport?.value) {
      console.error('No AWS links found for product:', product.title);
      throw new Error(`No AWS links found for product: ${product.title}`);
    }

    const awsLinks = JSON.parse(product.awsLinkExport.value).aws_link || [];
    const nonPreviewLinks = awsLinks.filter((l) => l && l.variant && l.variant !== "preview_link");

    if (nonPreviewLinks.length === 0) {
      console.error('No non-preview links found for product:', product.title);
      throw new Error(`No non-preview links found for product: ${product.title}`);
    }

    const linkSelector = new MediaPreferenceLinkSelector();
    const chosen = linkSelector.selectBestLink(nonPreviewLinks, type);

    if (chosen) {
      const parts = chosen.variant.split("_");
      const format = (parts[0] || "unknown").toLowerCase();
      const resolution = (parts[1] || "unknown").toLowerCase();

      console.log('Chosen link for download:', chosen);
      console.log('Calling directDownload with:', {
        link: chosen.aws_link,
        title: product.title,
        format,
        resolution,
        type,
        producer: product.akeneoProducerText?.value || ""
      });

      await directDownload(chosen.aws_link, product.title, format, resolution, type, product.akeneoProducerText?.value || "");
      console.log('Download completed for:', product.title);
    } else {
      console.error('No suitable link chosen for product:', product.title);
      throw new Error(`No suitable link chosen for product: ${product.title}`);
    }
  }
}

/**
 * Media Preference Link Selector
 * Handles selecting the best download link based on user preferences
 */
class MediaPreferenceLinkSelector {
  /**
   * Select the best link based on Media Preferences
   */
  selectBestLink(nonPreviewLinks, type) {
    const isMotion = type.toLowerCase() === "motions";
    const isStill = type.toLowerCase() === "stills";
    const isVideo = ["motions", "countdowns", "mini movies"].includes(type.toLowerCase());

    const prefFormat = (isVideo ? (window.motionFormat || "") : "").toLowerCase();
    const prefResolution = (isVideo ? (window.motionResolution || "") : (window.stillResolution || "")).toLowerCase();
    const prefWidth = (isVideo ? (window.motionSize || "") : (window.stillSize || "")).toLowerCase();

    if (isVideo) {
      return this.selectMotionLink(nonPreviewLinks, prefFormat, prefResolution, prefWidth);
    } else if (isStill) {
      return this.selectStillLink(nonPreviewLinks, prefResolution, prefWidth);
    } else {
      return nonPreviewLinks[0]; // For other types, use first available
    }
  }

  /**
   * Select link for motion products
   */
  selectMotionLink(nonPreviewLinks, prefFormat, prefResolution, prefWidth) {
    const hasAnyPref = !!(prefFormat || prefResolution || prefWidth);

    if (hasAnyPref) {
      // Exact match: all provided preferences must match
      const exactMatches = nonPreviewLinks.filter((link) => {
        const [fmt = "", res = "", width = "sw"] = link.variant.toLowerCase().split("_");
        if (prefFormat && fmt !== prefFormat) return false;
        if (prefResolution && res !== prefResolution) return false;
        if (prefWidth && width !== prefWidth) return false;
        return true;
      });

      if (exactMatches.length > 0) {
        return exactMatches[0];
      }

      // Format + resolution match (ignore width preference)
      if (prefFormat && prefResolution) {
        const fmtResMatches = nonPreviewLinks.filter((link) => {
          const [fmt = "", res = ""] = link.variant.toLowerCase().split("_");
          return fmt === prefFormat && res === prefResolution;
        });
        if (fmtResMatches.length > 0) return fmtResMatches[0];
      }

      // Format-only match — most critical preference is the container format
      if (prefFormat) {
        const fmtMatches = nonPreviewLinks.filter((link) => {
          const [fmt = ""] = link.variant.toLowerCase().split("_");
          return fmt === prefFormat;
        });
        if (fmtMatches.length > 0) return fmtMatches[0];
      }
    }

    return nonPreviewLinks[0]; // Last resort: first available
  }

  /**
   * Select link for still products
   */
  selectStillLink(nonPreviewLinks, prefResolution, prefWidth) {
    const hasAnyPref = !!(prefResolution || prefWidth);

    if (hasAnyPref) {
      const exactMatches = nonPreviewLinks.filter((link) => {
        const parts = link.variant.toLowerCase().split("_");
        const res = parts[1] || "";
        const width = parts[2] || "sw";
        if (prefResolution && res !== prefResolution) return false;
        if (prefWidth && width !== prefWidth) return false;
        return true;
      });

      if (exactMatches.length > 0) {
        return exactMatches[0];
      }
    }

    return nonPreviewLinks[0]; // Use first available
  }
}

// Add CSS styles for bulk mode
function addBulkModeStyles() {
  const styleId = 'bulk-mode-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Bulk mode styles moved to CSS file */
    
    /* Spinning animation for loading indicators */
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

// Initialize the multi-select download manager when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Add styles first
  addBulkModeStyles();

  // Wait for the grouped products to be loaded
  setTimeout(() => {
    if (typeof productPackArray !== 'undefined' && productPackArray.length > 0) {
      const manager = new MultiSelectDownloadManager();

      // Only initialize if the page has the required elements
      if (manager.hasRequiredElements()) {
        window.multiSelectDownloadManager = manager;
        window.multiSelectDownloadManager.init();
        console.log('Multi-select download manager initialized');
      } else {
        console.log('Bulk download not available on this page - missing product-additional-section');
      }
    }
  }, 1000);
});
